import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { translation } from '@cue/translations';
import { cueExclamation, IconComponent } from '@cue/ui/icons';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import {
  CalendarWorkingHours,
  findIanaWindowsMappingFromUnkown,
  getCalendarWorkingHoursStatusAt,
} from '@cue/calendars';
import { TimeFormatPipe } from '@cue/pipes';
import { getTimeZoneFromOfficeString } from '@cue/data';
import { ExpansionPanelComponent, ExpansionPanelTitleDirective } from '@progress/kendo-angular-layout';

export type WorkingHourInfo = {
  workingHours: CalendarWorkingHours;
};

@Component({
  selector: 'cue-working-hours',
  imports: [IconComponent, TranslocoPipe, TimeFormatPipe, ExpansionPanelComponent, ExpansionPanelTitleDirective],
  templateUrl: './working-hours.component.html',
  styleUrls: ['./working-hours.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkingHoursComponent {
  workingHourInfo = input.required<WorkingHourInfo>();
  relativeNow = input<Date>();
  expensionPanelClass = input<string>('show-panel');

  now = computed(() => {
    return this.relativeNow() || new Date();
  });

  ICONS = {
    cueExclamation,
  };

  resourceTimeZone = computed(() =>
    this.workingHourInfo().workingHours.timeZoneInfo === 'Custom time zone'
      ? 'UTC'
      : this.workingHourInfo().workingHours.timeZoneInfo
  );

  ianaTimeZone = computed(() => {
    return findIanaWindowsMappingFromUnkown(this.resourceTimeZone())?.iana[0] ?? this.resourceTimeZone();
  });

  isDifferentTimezone = computed(() => {
    // ofsset from IANA timezone name
    const offset = getTimezoneOffset(this.ianaTimeZone());
    const offset2 = getTimezoneOffset(this.currentIanaTimezone());
    return offset !== offset2;
  });

  currentIanaTimezone = computed(() => {
    return findIanaWindowsMappingFromUnkown(Intl.DateTimeFormat().resolvedOptions().timeZone)?.iana[0] ?? 'UTC';
  });

  daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  daysForLocale = computed(() => {
    const resourceTimeZone = getTimeZoneFromOfficeString(this.resourceTimeZone()) ?? this.resourceTimeZone();
    const startTimeDay = formatInTimeZone(this.now(), resourceTimeZone, 'iiii').toLowerCase();

    return this.daysOfWeek.map((day, dayIndex) => {
      const foundDaysOfWeek = this.workingHourInfo().workingHours.workingHoursSetting.filter((d) => d.day === dayIndex);
      const active = foundDaysOfWeek.length > 0;
      const slots = active
        ? foundDaysOfWeek.map((slot) => {
            // STart hours
            const startHour = new Date();

            let hours = parseInt(slot.startPeriod.split(':')[0]);
            let minutes = parseInt(slot.startPeriod.split(':')[1]);
            startHour.setHours(Number(hours));
            startHour.setMinutes(Number(minutes));

            const endHour = new Date();
            hours = parseInt(slot.endPeriod.split(':')[0]);
            minutes = parseInt(slot.endPeriod.split(':')[1]);
            if (hours === 24) {
              hours = 23;
              minutes = 59;
            }
            endHour.setHours(Number(hours));
            endHour.setMinutes(Number(minutes));

            return {
              start: startHour,
              end: endHour,
            };
          })
        : [];

      return {
        display: day,
        active: active,
        today: day === startTimeDay,
        slots: slots,
      };
    });
  });

  alwaysClosed = computed(() => {
    return this.tillStart().hours === -1;
  });

  accordionExpanded = false;
  translation = translation;

  getDayTranslation(day: string) {
    return this.translation.workingHours[day];
  }

  openClosedCaption = computed(() => {
    if (this.alwaysClosed()) {
      return this.translation.workingHours.alwaysClosed;
    }

    if (this.tillStart().hours === 0 && this.tillStart().minutes === 0) {
      return !this.relativeNow() ? this.translation.workingHours.open : this.translation.workingHours.openEvent;
    } else {
      return !this.relativeNow() ? this.translation.workingHours.closed : this.translation.workingHours.closedEvent;
    }
  });

  whenOpensCaption = computed(() => {
    return !this.relativeNow() ? this.translation.workingHours.whenOpens : this.translation.workingHours.whenOpensEvent;
  });

  isOpen = computed(() => {
    return this.tillStart().hours === 0 && this.tillStart().minutes === 0;
  });

  tillStart = computed(() => {
    const resourceTimeZone = getTimeZoneFromOfficeString(this.resourceTimeZone()) ?? this.resourceTimeZone();
    const status = getCalendarWorkingHoursStatusAt({
      workingHours: this.workingHourInfo().workingHours.workingHoursSetting,
      timeZone: resourceTimeZone,
      at: this.now(),
    });

    if (status.isOpen) {
      return { hours: 0, minutes: 0 };
    }

    if (status.minutesUntilNextOpen === null) {
      return { hours: -1, minutes: -1 };
    }

    return {
      hours: Math.floor(status.minutesUntilNextOpen / 60),
      minutes: status.minutesUntilNextOpen % 60,
    };
  });

  resourceTimeZoneOffset = computed(() => {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const offset = getTimezoneOffset(this.ianaTimeZone()) / 1000 / 60;
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset < 0 ? '-' : '+';
    return `${sign}${hours.toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    })}:${minutes.toLocaleString(locale, { minimumIntegerDigits: 2, useGrouping: false })}`;
  });
}
